/**
 * @file Settings-owned app permission normalization helpers.
 */

export const ADMIN_APP_BOARD_PERMISSIONS = Object.freeze([
  "calendar",
  "patients",
  "labs",
  "recalls",
  "projects",
  "assets",
  "maintenance",
  "ordering",
  "compliance",
  "drugs",
]);

export const COLLECTION_PERMISSION_OPTIONS = Object.freeze([
  { value: "calendar", label: "Calendar" },
  { value: "patients", label: "Patients" },
  { value: "labs", label: "Labs", impliedBy: "patients" },
  { value: "recalls", label: "Recalls" },
  { value: "projects", label: "Projects" },
  { value: "assets", label: "Assets" },
  { value: "maintenance", label: "Maintenance", impliedBy: "assets" },
  { value: "ordering", label: "Ordering" },
  { value: "compliance", label: "Compliance" },
  { value: "drugs", label: "Drugs" },
]);

const ALLOWED_APP_PERMISSIONS = new Set(ADMIN_APP_BOARD_PERMISSIONS);

const IMPLIED_PERMISSION_BY_PARENT = Object.freeze({
  patients: "labs",
  assets: "maintenance",
});

/**
 * Adds implied child permissions to a permission list.
 * @param {Array<string>} permissions Permission IDs.
 * @returns {Array<string>} Permission IDs with implied children included.
 */
export function applyPermissionImplications(permissions = []) {
  const normalized = new Set(
    (Array.isArray(permissions) ? permissions : [])
      .filter((permission) => ALLOWED_APP_PERMISSIONS.has(permission)),
  );
  Object.entries(IMPLIED_PERMISSION_BY_PARENT).forEach(([parent, child]) => {
    if (normalized.has(parent)) {
      normalized.add(child);
    }
  });
  return [...normalized];
}

/**
 * Returns canonical app permissions for a user role.
 * @param {string} role User role.
 * @param {Array<string>} permissions Permission IDs.
 * @returns {Array<string>} Normalized permission IDs.
 */
export function getNormalizedUserPermissions(role, permissions = []) {
  const impliedPermissions = applyPermissionImplications(permissions);
  if (role !== "admin") {return impliedPermissions;}
  return [
    ...new Set([
      ...impliedPermissions,
      ...ADMIN_APP_BOARD_PERMISSIONS,
    ]),
  ];
}

/**
 * Returns whether a permission is implied by selected parent permissions.
 * @param {Array<string>} permissions Permission IDs.
 * @param {string} permission Permission to test.
 * @returns {boolean} True when a selected parent implies this permission.
 */
export function isPermissionImplied(permissions = [], permission) {
  if (!permission) {return false;}
  const selected = new Set(Array.isArray(permissions) ? permissions : []);
  return Object.entries(IMPLIED_PERMISSION_BY_PARENT).some(
    ([parent, child]) => child === permission && selected.has(parent),
  );
}

/**
 * Returns whether a stored child permission should be locked by its parent.
 * Legacy users may have a parent permission without the stored child; in that
 * case the child must remain assignable until the corrected set is saved.
 * @param {Array<string>} permissions Permission IDs.
 * @param {string} permission Permission to test.
 * @returns {boolean} True when the stored child is locked by a stored parent.
 */
export function isPermissionLockedByParent(permissions = [], permission) {
  if (!permission) {return false;}
  const selected = new Set(Array.isArray(permissions) ? permissions : []);
  return selected.has(permission) && isPermissionImplied(permissions, permission);
}
